import React, { MouseEventHandler, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useLocation } from 'react-router';
import { NAFGTMEvent } from '@appTypes/GTM/NAFGTMEvent';
import { resolveLinkProps, isResolvedInternal, ExternalLinkProps } from '@lib/resolveLinkProps';
import useSelector from '../../../../redux/typedHooks';
import { useGTMDataLayer } from '../../../../hooks/GTM/useGTMDataLayer';
import { useMessageTrackingContext } from '../../../../pages/MyMessages/contexts/MessageTrackingContext';

interface LinkProps {
  value?: { href: string; blank: boolean };
  children: ReactNode | ReactNode[] | string | string[];
}

export const LinkMarks = ({ value, children }: LinkProps) => {
  const { blank, href } = value;
  const baseUrl = useSelector((state) => state.application.baseUrl);
  const location = useLocation();
  const dataLayer = useGTMDataLayer();
  const messageContext = useMessageTrackingContext();
  const hostname = (typeof window !== 'undefined' && window.location.hostname) || undefined;

  const linkText = Array.isArray(children) ? children.join(' ') : children.toString();

  const trackLinkClick = (trackUrl: string, trackHostname?: string) => {
    dataLayer?.push({
      event: messageContext ? NAFGTMEvent.mittNafMessageCtaClick : 'link_click',
      link_text: linkText,
      link_location: location.pathname,
      link_url: trackUrl,
      link_hostname: trackHostname || hostname,
      ...(messageContext?.messageId ? { message_id: messageContext.messageId } : {}),
      ...(messageContext?.messageType ? { message_type: messageContext.messageType } : {}),
      ...(messageContext?.messageTitle ? { message_title: messageContext.messageTitle } : {}),
    });
  };

  const linkProps = resolveLinkProps({ url: href, baseUrl, blank });

  if (isResolvedInternal(linkProps)) {
    const handleTracking: MouseEventHandler<HTMLAnchorElement> = () => {
      trackLinkClick(href);
    };

    return (
      <Link to={linkProps.to} onClick={handleTracking}>
        {children}
      </Link>
    );
  }

  const externalProps = linkProps as ExternalLinkProps;

  if (externalProps.href) {
    const handleExternalTracking: MouseEventHandler<HTMLAnchorElement> = (e) => {
      try {
        const url = new URL(externalProps.href);
        trackLinkClick(externalProps.href, url.hostname);
      } catch {
        trackLinkClick(externalProps.href);
      }

      if (blank) {
        if (e.currentTarget.href) {
          window.open(e.currentTarget.href);
        }
      } else {
        e.preventDefault();
        if (e.currentTarget.href) {
          window.location.href = e.currentTarget.href;
        }
      }
    };

    return (
      <a
        href={externalProps.href}
        target={externalProps.target}
        rel={externalProps.rel}
        onClick={handleExternalTracking}
      >
        {children}
      </a>
    );
  }

  return <span>{children}</span>;
};
